<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Functional Assignments
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip" class="absolute z-10 invisible inline-block w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2 flex flex-col gap-1">
                        <p>
                            In the <strong>Functions</strong> section, you will see all identified functions from Step 3. In the <strong>Threat/Hazard</strong> section, you will see a drop-down that contains all threats/hazards identified in Step 2. To associate a <strong>Function</strong> to a <strong>Hazard</strong>, simply select the appropriate hazard from the drop-down. Beside the <strong>Function</strong> you would like to associate, click on 
                            <span class="inline-flex w-8 h-8 flex flex-row items-center justify-center rounded-md border border-nside-orange-100 bg-gradient-to-b from-nside-orange-300 to-nside-orange-400">
                                <lucide-icon [name]="icons.Plus" [size]="20" class="text-white"></lucide-icon>
                            </span>. When completed associating all functions for a <strong>Hazard</strong>, click on 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>. 
                        </p>
                        <p>
                            To remove an association from a <strong>Hazard</strong>, simply click 
                            <span class="inline-flex h-8 w-8 flex flex-row gap-2 items-center justify-center rounded-md border border-nside-gray-100 bg-gradient-to-b from-nside-gray-100 to-nside-gray-200">
                                <lucide-icon [name]="icons.X" [size]="20" class="text-white"></lucide-icon>
                            </span> beside the Function you would like to remove. Click 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>.
                        </p>
                        <p>
                            Once all <strong>Functional Assignments</strong> have been completed, continue forward to begin creating your <strong>Courses of Action</strong>.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                [sectionId]="sectionTypeId"
            ></eop-assistant>
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between">
                <h2 class="oswald-font text-3xl font-medium">FUNCTIONS</h2>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full flex flex-col gap-2">
                    <div *ngIf="loadingFunction">
                        <cards-list-skeleton></cards-list-skeleton>
                    </div>
                    <ng-container *ngIf="!loadingFunction">
                        <ng-container *ngIf="functionsList.length > 0; else emptyFunctionsList">
                            <div 
                                *ngFor="let function of functionsList"
                                class="w-full flex flex-row items-stretch gap-2"
                            >
                                <button
                                    class="min-h-10 flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md bg-gray-50 border border-nside-gray-300 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100 group"
                                    (click)="$event.preventDefault(); handleOpenModal('function-details', function);"
                                >
                                    <span class="min-w-0 flex-1 text-start whitespace-pre-line break-words [overflow-wrap:anywhere]">
                                        {{ function.function_name }}
                                    </span>
                                </button>
                                <button
                                    *ngIf="canSave()"
                                    class="w-10 flex flex-row items-center justify-center rounded-md border border-nside-orange-100 bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200"
                                    (click)="$event.preventDefault(); addFunctionToThreat(function);"
                                >
                                    <lucide-icon [name]="icons.Plus" [size]="20" class="text-white"></lucide-icon>
                                </button>
                            </div>
                        </ng-container>
                    </ng-container>
                    <ng-template #emptyFunctionsList>
                        <div class="text-nside-gray-100 italic">
                            Empty Function list
                        </div>
                    </ng-template>
                </div>
            </div>                
        </div>
        <div class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <h2 class="oswald-font text-3xl">THREAT/HAZARD</h2>
            <ng-container *ngIf="threatsList.length > 0; else emptyFunctionsList">            
                <div class="w-screen-md flex flex-col gap-2 group">
                    <select 
                        id="threats" 
                        class="border border-nside-gray-300 font-medium rounded-md focus:ring-nside-gray-300 focus:border-nside-gray-300 block w-full p-2.5"
                        [(ngModel)]="selectedThreatId" 
                        (ngModelChange)="onThreatChange($event)"
                    >
                        <option *ngFor="let threat of threatsList" [value]="threat.id">
                            {{ threat.threat }}
                        </option>
                    </select>
                </div>
                <div class="w-full p-4 min-h-32 max-h-full rounded-md border-2 border-nside-gray-300 flex flex-grow overflow-y-auto">
                    <div class="h-full flex flex-col gap-2 text-gray-900 overflow-y-auto">
                        <ng-container *ngIf="selectedFunctionsList.length > 0; else emptySelectedFunctionsList">            
                            <div 
                                *ngFor="let selectedFunction of selectedFunctionsList"
                                class="w-full flex flex-row items-stretch gap-2"
                            >
                                <p
                                    class="flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center rounded-md bg-gray-50 border border-nside-gray-300 text-start"
                                >
                                    {{ selectedFunction.function_name }}
                                </p>
                                <button
                                    class="h-10 w-10 flex flex-row gap-2 items-center justify-center rounded-md border border-nside-gray-100 bg-gradient-to-b from-nside-gray-100 to-nside-gray-200 hover:bg-gradient-to-b hover:from-nside-gray-300 hover:to-nside-gray-400"
                                    (click)="$event.preventDefault(); removeFunctionFromThreat(selectedFunction);"
                                >
                                    <lucide-icon [name]="icons.X" [size]="20" class="text-white"></lucide-icon>
                                </button>
                            </div>
                        </ng-container>
                        <ng-template #emptySelectedFunctionsList>
                            <div class="w-full text-nside-gray-100 italic">
                                Assign function(s) to this threat/hazard.
                            </div>
                        </ng-template>
                    </div>
                </div>
                <div class="w-full flex flex-row gap-2 items-center justify-end">
                    <div *ngIf="isLatestUpdateAvailable()" class="w-full flex gap-1 items-start justify-start">
                        <p class="oswald-font font-normal">
                            Last Updated At: <span class="raleway-font">{{  latestUpdate.last_updated_at | date: 'MM/dd/yyyy - HH:mm' }}</span>
                        </p>
                        <p class="oswald-font font-normal">
                            By: <span class="raleway-font">{{ latestUpdate.last_updated_user_name || 'None' }}</span>
                        </p>
                    </div>
                    <button 
                        *ngIf="canSave()"
                        class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                        [ngClass]="{
                            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !selectedThreatId || !hasChanges(),
                            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': selectedThreatId && hasChanges()
                        }"
                        [disabled]="!selectedThreatId || !hasChanges()"
                        (click)="$event.preventDefault(); saveSelectedFunctions();"
                    >
                        Save
                    </button>
                </div>
            </ng-container>
            <ng-template #emptyFunctionsList>
                <div class="text-nside-gray-100 italic">
                    Empty Functional Assignment
                </div>
            </ng-template>
        </div>
    </div>
    
    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <introduction></introduction>
    
    <function-details
        *ngIf="showFunctionDetails"
        [functionDetails]="selectedFunction"
        (onClose)="handleCloseModal('function-details')"
    ></function-details>

    <popup-message 
        [type]="messageModal"
        [message]="popupMessage"
        (close)="resetMessageModal()"
    ></popup-message>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />

</main>